import '@styles/features/locations.css';
import { LOW_STOCK_HINT } from '@constants/limits';
import { ROUTES } from '@constants/routes';
import { locationTypeLabel } from '@locales/id/labels';
import { NETWORK as T } from '@locales/id/messages';
import { num, shortRegency } from '@lib/format';
import { Async, Chip, EmptyState, ListRow, OffsetPager, SkeletonCards } from '@ui';
import { fillLine } from '@features/locations/model/network';
import type { NetworkPage } from '@features/locations/model/useNetworkPage';

/** A calm word on the stock, never an alarm: empty, or running low (the thresholds of phase 2 will refine it). */
function StockHint({ stock }: { stock: number }) {
  if (stock === 0) return <Chip tone="amber">{T.empty}</Chip>;
  if (stock <= LOW_STOCK_HINT) return <span className="network-low">{T.low}</span>;
  return null;
}

/**
 * Locations of the distribution network, a page at a time exactly as the server pages them: name ("Lokasi Anda" on
 * the account's own), type and code, the regency, and the stock with a calm hint when it is empty or low. Each row
 * opens the location.
 */
export function NetworkList({ net, empty, ownId }: { net: NetworkPage; empty: string; ownId?: number }) {
  return (
    <Async state={net.state} skeleton={<SkeletonCards count={3} />} empty={<EmptyState compact title={empty} icon="pin" />}>
      {(page) => (
        <>
          <ul className="list network-list">
            {page.items.map((l) => (
              <ListRow
                key={l.id}
                to={ROUTES.location(l.id)}
                title={
                  <>
                    {l.name} {l.id === ownId && <Chip tone="blue">{T.own}</Chip>}
                  </>
                }
                meta={
                  <>
                    {locationTypeLabel(l.type)} · <span className="mono">{l.code}</span> · {shortRegency(l.regencyName)}
                  </>
                }
                trailing={
                  <span className="network-stock">
                    <span>
                      <b className="num">{num(l.stock)}</b> {T.cylinders}
                    </span>
                    {fillLine(l) && <span className="loc-fill-line">{fillLine(l)}</span>}
                    <StockHint stock={l.stock} />
                  </span>
                }
              />
            ))}
          </ul>
          {page.meta.totalPages > 1 && <OffsetPager meta={page.meta} count={page.items.length} onPage={net.setPage} noun={T.noun} />}
        </>
      )}
    </Async>
  );
}
